/**
 * A small curated emoji set for the picker (no dependency, no skin tones).
 * Each entry is the emoji, then its English short name and search keywords
 * (a few Portuguese ones too). Every emoji is a single pictographic
 * sequence, so the server accepts it as a reaction.
 */

export type EmojiCategoryId =
  | "smileys"
  | "people"
  | "nature"
  | "food"
  | "activities"
  | "travel"
  | "objects"
  | "symbols";

export interface EmojiEntry {
  emoji: string;
  /** English short name ("thumbs up"), used as the accessible name. */
  name: string;
  /** Lower-case words to search. */
  keywords: string[];
  category: EmojiCategoryId;
}

const RAW: Record<EmojiCategoryId, string[]> = {
  smileys: [
    "😀 grinning|smile happy feliz sorriso",
    "😃 smiley|smile happy joy",
    "😄 smile|happy laugh",
    "😁 grin|beaming teeth",
    "😆 laughing|lol haha",
    "😅 sweat smile|phew nervous",
    "🤣 rofl|rolling laugh lol",
    "😂 joy|tears laugh lol rindo",
    "🙂 slightly smiling|ok fine",
    "🙃 upside down|silly irony",
    "🫠 melting|hot embarrassed",
    "😉 wink|piscada",
    "😊 blush|happy shy",
    "😇 innocent|angel halo",
    "🥰 smiling hearts|love adore amor",
    "😍 heart eyes|love crush amor",
    "🤩 star struck|wow amazing",
    "😘 kissing heart|kiss beijo",
    "😋 yum|delicious tasty",
    "😛 tongue|playful",
    "😜 winking tongue|crazy joke",
    "🤪 zany|crazy goofy",
    "🤗 hugging|hug abraço thanks",
    "🤭 hand over mouth|oops giggle",
    "🫡 salute|respect ok sim",
    "🤫 shushing|quiet secret",
    "🤔 thinking|hmm think pensando",
    "🤨 raised eyebrow|skeptical suspicious",
    "😐 neutral|meh",
    "😑 expressionless|blank",
    "😶 no mouth|speechless silent",
    "😏 smirk|smug",
    "🙄 eye roll|whatever",
    "😬 grimacing|awkward eek",
    "😌 relieved|calm peace",
    "😴 sleeping|tired sleep zzz sono",
    "🤤 drooling|want",
    "😷 mask|sick doente",
    "🤒 thermometer|sick fever",
    "🤯 mind blown|exploding wow",
    "🥳 partying|party celebrate festa",
    "😎 sunglasses|cool",
    "🤓 nerd|geek",
    "🧐 monocle|inspect curious",
    "😕 confused|unsure",
    "🙁 frowning|sad",
    "😮 open mouth|surprised wow",
    "😲 astonished|shocked",
    "😳 flushed|embarrassed",
    "🥺 pleading|please puppy",
    "🥲 smiling tear|grateful bittersweet",
    "😢 crying|sad tear triste",
    "😭 sobbing|cry sad",
    "😱 screaming|scared shock",
    "😖 confounded|frustrated",
    "😞 disappointed|sad",
    "😓 downcast sweat|hard work",
    "😩 weary|tired",
    "😤 triumph|huff frustrated",
    "😡 pouting|angry mad raiva",
    "🤬 cursing|angry swearing",
    "💀 skull|dead lol",
    "👻 ghost|boo",
    "🤖 robot|bot agent agente",
    "👽 alien|ufo",
    "🙈 see no evil|monkey oops",
    "🙉 hear no evil|monkey",
    "🙊 speak no evil|monkey secret",
  ],
  people: [
    "👍 thumbs up|like yes ok good +1 sim",
    "👎 thumbs down|dislike no bad -1 não",
    "👏 clap|applause bravo parabéns",
    "🙌 raising hands|celebrate hooray",
    "🙏 folded hands|please thanks pray obrigado",
    "👋 wave|hello hi bye oi tchau",
    "🤝 handshake|deal agreement acordo",
    "✌️ victory|peace",
    "🤞 crossed fingers|luck hope",
    "👌 ok hand|perfect okay",
    "🤌 pinched fingers|italian what",
    "🤙 call me|shaka hang loose",
    "💪 flexed biceps|strong strength força",
    "👀 eyes|look see watching olhando",
    "🧠 brain|smart think",
    "👉 point right|this",
    "👈 point left",
    "👆 point up|above",
    "👇 point down|below",
    "☝️ index up|one first",
    "✋ raised hand|stop high five",
    "🖐️ hand splayed|five",
    "🖖 vulcan|spock",
    "✍️ writing hand|write",
    "🫶 heart hands|love thanks",
    "🤷 shrug|dunno whatever",
    "🤦 facepalm|ugh",
    "🙋 raising hand|me question",
    "🙇 bowing|sorry thanks",
    "🧑‍💻 technologist|developer coder dev programmer",
    "🕺 dancing man|dance party",
    "💃 dancing woman|dance party",
    "🏃 running|run hurry",
    "🚶 walking|walk",
    "🧘 yoga|calm meditate",
    "👶 baby|new",
    "🧑 person|human pessoa",
    "👥 people|users team equipe",
  ],
  nature: [
    "🐶 dog|puppy cachorro",
    "🐱 cat|kitten gato",
    "🐭 mouse",
    "🐹 hamster",
    "🐰 rabbit|bunny",
    "🦊 fox|raposa",
    "🐻 bear|urso",
    "🐼 panda",
    "🐨 koala",
    "🐯 tiger|tigre",
    "🦁 lion|leão",
    "🐮 cow|vaca",
    "🐷 pig|porco",
    "🐸 frog|sapo",
    "🐵 monkey|macaco",
    "🐔 chicken|galinha",
    "🐧 penguin|pinguim",
    "🐦 bird|pássaro",
    "🦆 duck|pato",
    "🦉 owl|coruja",
    "🐝 bee|abelha honeybee",
    "🐛 bug|insect",
    "🦋 butterfly|borboleta",
    "🐌 snail|slow lento",
    "🐞 lady beetle|bug ladybug",
    "🐢 turtle|slow tartaruga",
    "🐍 snake|python cobra",
    "🐙 octopus|polvo",
    "🐠 fish|peixe",
    "🐬 dolphin|golfinho",
    "🐳 whale|baleia docker",
    "🦈 shark|tubarão",
    "🌵 cactus",
    "🌲 evergreen|tree árvore",
    "🌳 tree|árvore",
    "🌴 palm tree|beach",
    "🌱 seedling|grow plant",
    "🌿 herb|plant",
    "🍀 clover|luck sorte",
    "🍁 maple leaf|autumn",
    "🍂 fallen leaves|autumn",
    "🌸 cherry blossom|flower spring flor",
    "🌻 sunflower|flower girassol",
    "🌹 rose|flower rosa",
    "🌷 tulip|flower",
    "🌞 sun face|sunny sol",
    "🌝 full moon face|moon",
    "🌙 crescent moon|night lua",
    "☀️ sun|sunny weather sol",
    "⛅ sun behind cloud|cloudy",
    "🌧️ rain|cloud chuva",
    "⛈️ storm|thunder",
    "❄️ snowflake|cold winter frio",
    "🌈 rainbow|arco-íris",
    "🌊 wave|ocean sea mar",
    "💧 droplet|water água",
  ],
  food: [
    "🍎 apple|maçã",
    "🍐 pear|pera",
    "🍊 tangerine|orange laranja",
    "🍋 lemon|limão",
    "🍌 banana",
    "🍉 watermelon|melancia",
    "🍇 grapes|uva",
    "🍓 strawberry|morango",
    "🍒 cherries|cereja",
    "🍑 peach|pêssego",
    "🥭 mango|manga",
    "🍍 pineapple|abacaxi",
    "🥥 coconut|coco",
    "🥝 kiwi",
    "🍅 tomato|tomate",
    "🥑 avocado|abacate",
    "🥦 broccoli|brócolis",
    "🌽 corn|milho",
    "🥕 carrot|cenoura",
    "🌶️ hot pepper|spicy pimenta",
    "🥐 croissant",
    "🍞 bread|pão",
    "🧀 cheese|queijo",
    "🥚 egg|ovo",
    "🍳 cooking|fried egg",
    "🥓 bacon",
    "🍔 hamburger|burger",
    "🍟 fries|batata",
    "🍕 pizza",
    "🌭 hot dog|cachorro-quente",
    "🌮 taco",
    "🌯 burrito",
    "🥗 salad|salada",
    "🍝 spaghetti|pasta macarrão",
    "🍜 ramen|noodles",
    "🍣 sushi",
    "🍱 bento",
    "🍩 doughnut|donut",
    "🍪 cookie|biscoito",
    "🎂 birthday cake|aniversário bolo",
    "🍰 cake|bolo",
    "🍫 chocolate",
    "🍿 popcorn|pipoca",
    "🍦 ice cream|sorvete",
    "☕ coffee|café",
    "🍵 tea|chá",
    "🧃 juice box|suco",
    "🍺 beer|cerveja",
    "🍻 cheers|beers saúde",
    "🥂 clinking glasses|cheers toast brinde",
    "🍷 wine|vinho",
    "🍹 tropical drink|cocktail",
    "🧉 mate|chimarrão",
  ],
  activities: [
    "🎉 party popper|tada celebrate congrats festa",
    "🎊 confetti|celebrate",
    "🎈 balloon|party",
    "🎁 gift|present presente",
    "🏆 trophy|win winner troféu",
    "🥇 first place|gold medal",
    "🥈 second place|silver medal",
    "🥉 third place|bronze medal",
    "🏅 medal|sports",
    "⚽ soccer|football futebol",
    "🏀 basketball|basquete",
    "🏈 american football",
    "⚾ baseball",
    "🎾 tennis|tênis",
    "🏐 volleyball|vôlei",
    "🏓 ping pong|table tennis",
    "🏸 badminton",
    "⛳ golf",
    "🎯 bullseye|target goal dart alvo meta",
    "🎳 bowling",
    "🎮 video game|controller gaming",
    "🕹️ joystick|arcade",
    "🎲 dice|game dado",
    "♟️ chess|xadrez",
    "🧩 puzzle|piece",
    "🎨 palette|art design arte",
    "🎭 theater|drama",
    "🎬 clapper|movie film filme",
    "🎤 microphone|sing karaoke",
    "🎧 headphones|music fone",
    "🎸 guitar|violão",
    "🎹 piano|keyboard",
    "🥁 drum|bateria",
    "🎺 trumpet",
    "🏋️ weight lifting|gym",
    "🚴 cycling|bike",
    "🏊 swimming|natação",
    "🧗 climbing|escalada",
    "🛹 skateboard",
  ],
  travel: [
    "🚗 car|carro",
    "🚕 taxi",
    "🚌 bus|ônibus",
    "🏎️ racing car|fast",
    "🚓 police car|polícia",
    "🚑 ambulance|ambulância",
    "🚒 fire engine|bombeiros",
    "🚚 truck|delivery caminhão",
    "🚜 tractor|trator",
    "🛵 scooter|moto",
    "🚲 bicycle|bike",
    "🚂 locomotive|train trem",
    "🚇 metro|subway",
    "✈️ airplane|flight travel avião viagem",
    "🛫 departure|takeoff",
    "🛬 arrival|landing",
    "🚀 rocket|launch ship deploy foguete",
    "🛸 flying saucer|ufo",
    "🚁 helicopter",
    "⛵ sailboat|barco",
    "🚤 speedboat",
    "⚓ anchor|âncora",
    "🚧 construction|wip work in progress obra",
    "⛽ fuel pump|gas",
    "🗺️ world map|mapa",
    "🗽 statue of liberty|new york",
    "🗼 tower|tokyo",
    "🏰 castle|castelo",
    "🏟️ stadium|estádio",
    "🎡 ferris wheel",
    "🎢 roller coaster|montanha-russa",
    "🏖️ beach|praia vacation férias",
    "🏝️ island|ilha",
    "🏔️ mountain|snow montanha",
    "🌋 volcano|vulcão",
    "🏕️ camping",
    "🏠 house|home casa",
    "🏢 office|building escritório",
    "🏭 factory|fábrica",
    "🏥 hospital",
    "🏦 bank|banco",
    "🏫 school|escola",
    "🌆 cityscape|city cidade",
    "🌉 bridge at night|ponte",
  ],
  objects: [
    "⌚ watch|time relógio",
    "📱 mobile phone|celular",
    "💻 laptop|computer notebook",
    "⌨️ keyboard|teclado",
    "🖥️ desktop|computer monitor",
    "🖨️ printer|impressora",
    "🖱️ mouse|computer",
    "💾 floppy disk|save salvar",
    "📷 camera|photo foto",
    "🎥 movie camera|video",
    "📞 telephone receiver|call ligação",
    "📺 television|tv",
    "⏰ alarm clock|despertador",
    "⏳ hourglass|wait loading espera",
    "🔋 battery|bateria",
    "🔌 plug|power",
    "💡 bulb|idea ideia",
    "🔦 flashlight|lanterna",
    "🕯️ candle|vela",
    "💸 money with wings|spend",
    "💵 dollar|money cash dinheiro",
    "💰 money bag|dinheiro",
    "💳 credit card|cartão payment",
    "💎 gem|diamond",
    "⚖️ balance scale|justice",
    "🔧 wrench|fix tool chave",
    "🔨 hammer|build martelo",
    "🛠️ tools|build fix ferramentas",
    "⚙️ gear|settings config engrenagem",
    "🔩 nut and bolt",
    "🧰 toolbox|ferramentas",
    "🧲 magnet|ímã",
    "🔬 microscope|science",
    "🔭 telescope",
    "📡 satellite antenna|signal",
    "💊 pill|medicine remédio",
    "🔑 key|password chave",
    "🔒 locked|secure lock cadeado",
    "🔓 unlocked|open",
    "📦 package|box ship release pacote",
    "📬 mailbox|mail",
    "✉️ envelope|email carta",
    "📧 email|e-mail",
    "📝 memo|note write notes nota",
    "📄 page|document documento",
    "📑 bookmark tabs|docs",
    "📊 bar chart|stats data gráfico",
    "📈 chart increasing|growth up subir",
    "📉 chart decreasing|down cair",
    "📅 calendar|date calendário",
    "🗓️ spiral calendar|schedule agenda",
    "📌 pushpin|pin fixar",
    "📍 round pushpin|location",
    "📎 paperclip|attachment anexo clip",
    "✂️ scissors|cut tesoura",
    "✏️ pencil|edit lápis",
    "🖊️ pen|caneta",
    "📚 books|library livros",
    "📖 open book|read livro",
    "🔖 bookmark|marcador",
    "🏷️ label|tag etiqueta",
    "🧾 receipt|invoice recibo",
    "📁 folder|pasta",
    "🗂️ card index dividers|organize",
    "🗑️ wastebasket|trash delete lixo",
    "🔍 magnifying glass|search find buscar",
    "🧪 test tube|test experiment teste",
    "🧯 fire extinguisher|incident",
    "🛒 shopping cart|cart compras",
    "🎒 backpack|mochila",
  ],
  symbols: [
    "❤️ red heart|love like amor coração",
    "🧡 orange heart",
    "💛 yellow heart",
    "💚 green heart",
    "💙 blue heart",
    "💜 purple heart",
    "🖤 black heart",
    "🤍 white heart",
    "💔 broken heart|sad",
    "💕 two hearts|love",
    "💖 sparkling heart|love",
    "💯 hundred|100 perfect score",
    "✅ check mark button|done yes ok feito",
    "☑️ check box|done",
    "✔️ check mark|done yes",
    "❌ cross mark|no wrong error erro",
    "❎ cross mark button|no",
    "⚠️ warning|caution alert atenção",
    "🚫 prohibited|no forbidden proibido",
    "⛔ no entry|stop blocked",
    "❓ question|help pergunta dúvida",
    "❗ exclamation|important",
    "‼️ double exclamation|important urgent",
    "⁉️ interrobang|what",
    "💤 zzz|sleep",
    "💢 anger|angry",
    "💬 speech balloon|comment chat message",
    "💭 thought balloon|thinking",
    "♻️ recycle|again refactor",
    "⭐ star|favorite estrela",
    "🌟 glowing star|shine",
    "✨ sparkles|new shiny magic",
    "🔥 fire|hot lit fogo",
    "💥 collision|boom crash",
    "💫 dizzy|stars",
    "⚡ high voltage|fast lightning raio",
    "🔔 bell|notification sino",
    "🔕 bell slash|mute silence",
    "🎵 musical note|music",
    "➕ plus|add",
    "➖ minus|remove",
    "➡️ right arrow|next",
    "⬅️ left arrow|back",
    "⬆️ up arrow",
    "⬇️ down arrow",
    "↩️ right arrow curving left|reply undo",
    "🔁 repeat|loop again",
    "🔄 counterclockwise arrows|refresh sync",
    "▶️ play|start",
    "⏸️ pause",
    "⏹️ stop",
    "⏭️ next track|skip",
    "🔴 red circle|stop live",
    "🟠 orange circle",
    "🟡 yellow circle",
    "🟢 green circle|go ok online",
    "🔵 blue circle",
    "🟣 purple circle",
    "⚫ black circle",
    "⚪ white circle",
    "🏁 chequered flag|finish done",
    "🚩 triangular flag|red flag",
    "🏳️ white flag|surrender",
    "🏴 black flag",
  ],
};

export const EMOJI_CATEGORIES = Object.keys(RAW) as EmojiCategoryId[];

export const EMOJIS: EmojiEntry[] = EMOJI_CATEGORIES.flatMap((category) =>
  RAW[category].map((line) => {
    const space = line.indexOf(" ");
    const emoji = line.slice(0, space);
    const [name = "", words = ""] = line.slice(space + 1).split("|");
    const keywords = [...new Set(`${name} ${words}`.toLowerCase().split(/\s+/).filter(Boolean))];
    return { emoji, name, keywords, category };
  }),
);

const BY_EMOJI = new Map(EMOJIS.map((e) => [e.emoji, e]));

/** The entry of an emoji in the set (its name for an accessible label). */
export const emojiEntry = (emoji: string) => BY_EMOJI.get(emoji);

/**
 * Emoji whose name or keywords start with every word of the query
 * ("thumb up", "cora"), name matches first.
 */
export function searchEmoji(query: string, list: EmojiEntry[] = EMOJIS): EmojiEntry[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const hits = list.filter((e) => words.every((w) => e.keywords.some((k) => k.startsWith(w)) || e.emoji === w));
  const score = (e: EmojiEntry) => (e.name.toLowerCase().startsWith(words[0] ?? "") ? 0 : 1);
  return hits.sort((a, b) => score(a) - score(b));
}
